import CancelIcon from '@material-design-icons/svg/filled/cancel.svg?react';
import CheckBoxIcon from '@material-design-icons/svg/filled/check_box.svg?react';
import CheckBoxOutlineBlankIcon from '@material-design-icons/svg/filled/check_box_outline_blank.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import IndeterminateCheckBoxIcon from '@material-design-icons/svg/filled/indeterminate_check_box.svg?react';
import { isLeft, isRight } from 'fp-ts/Either';
import { useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../../../contexts/TRPC';
import {
  getAutomatedReviewBadgeClassName,
  getAutomatedReviewSummaryLabel,
} from '../../../../lib/hubspot-management/automatedReview';
import { formatDateTimeWithTimeFirst } from '../../../../lib/hubspot-management/formatters';
import { CollapsibleSection } from '../../CollapsibleSection';
import { InfoRow } from '../../InfoCard';
import { LoadingSection } from '../../layout/LoadingSection';
import { PanelSection } from '../../PanelSection';
import type {
  HubspotTicketAutomatedReviewData,
  HubspotTicketDetailData,
} from '../../types';

type OverrideConclusion = NonNullable<
  HubspotTicketAutomatedReviewData['checks'][number]['effectiveConclusion']
>;

interface HubspotTicketAutomatedReviewSectionProps {
  ticketId: string;
  summary: HubspotTicketDetailData['latestAutomatedReviewSummary'];
  latestPolicyVersion: string | null;
  review: HubspotTicketAutomatedReviewData | null;
  reviewError: Error | null;
  isReviewLoading: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /** Render fully expanded as a plain section (used inside the Checks tab). */
  alwaysExpanded?: boolean;
  trpc: TRPC | null;
  onRefetchReview: () => Promise<void>;
  onRefetchTicket: () => Promise<void>;
  onTicketUpdated?: () => Promise<void>;
}

const OVERRIDE_CONCLUSIONS: OverrideConclusion[] = [
  'Passed',
  'Warning',
  'Failed',
  'Skipped',
  'Error',
];

function stringifyReviewDetails(details: unknown) {
  if (details == null) {
    return null;
  }

  if (typeof details === 'string') {
    return details;
  }

  return JSON.stringify(details, null, 2);
}

function getReviewExecutorLabel(
  review: HubspotTicketAutomatedReviewData | null,
): string {
  if (review?.executor === 'github-actions') {
    return 'GitHub Actions';
  }

  if (review?.executor === 'dapp-testing') {
    return 'dapp-testing Worker';
  }

  if (review) {
    return 'Portal Worker (Legacy)';
  }

  return '—';
}

type CheckExecutionStatus =
  HubspotTicketAutomatedReviewData['checks'][number]['executionStatus'];
type CheckPresentationBadge = Parameters<
  typeof getAutomatedReviewBadgeClassName
>[0];
type ReviewCheck = HubspotTicketAutomatedReviewData['checks'][number];

function getCheckStatusPresentation(check: ReviewCheck): {
  badgeStatus: CheckPresentationBadge;
  label: string;
  Icon:
    | typeof CheckBoxIcon
    | typeof IndeterminateCheckBoxIcon
    | typeof CheckBoxOutlineBlankIcon
    | typeof CancelIcon
    | typeof ErrorIcon;
  iconClassName: string;
} {
  const executionStatus: CheckExecutionStatus = check.executionStatus;

  if (executionStatus === 'Queued') {
    return {
      badgeStatus: 'Queued',
      label: 'Queued',
      Icon: CheckBoxOutlineBlankIcon,
      iconClassName: 'text-sky-300',
    };
  }

  if (executionStatus === 'Running') {
    return {
      badgeStatus: 'Running',
      label: 'Running',
      Icon: IndeterminateCheckBoxIcon,
      iconClassName: 'text-amber-300',
    };
  }

  switch (check.effectiveConclusion) {
    case 'Passed':
      return {
        badgeStatus: 'Passed',
        label: 'Passed',
        Icon: CheckBoxIcon,
        iconClassName: 'text-emerald-300',
      };
    case 'Warning':
      return {
        badgeStatus: 'Running',
        label: 'Warning',
        Icon: IndeterminateCheckBoxIcon,
        iconClassName: 'text-amber-300',
      };
    case 'Skipped':
      return {
        badgeStatus: 'Stale',
        label: 'Skipped',
        Icon: CheckBoxOutlineBlankIcon,
        iconClassName: 'text-slate-300',
      };
    case 'Failed':
      return {
        badgeStatus: 'Failed',
        label: 'Failed',
        Icon: CancelIcon,
        iconClassName: 'text-red-300',
      };
    case 'Error':
      return {
        badgeStatus: 'Error',
        label: 'Error',
        Icon: ErrorIcon,
        iconClassName: 'text-rose-300',
      };
    default:
      return {
        badgeStatus: 'Queued',
        label: 'Completed',
        Icon: CheckBoxOutlineBlankIcon,
        iconClassName: 'text-slate-300',
      };
  }
}

export function HubspotTicketAutomatedReviewSection({
  ticketId,
  summary,
  latestPolicyVersion,
  review,
  reviewError,
  isReviewLoading,
  isExpanded,
  onToggle,
  alwaysExpanded = false,
  trpc,
  onRefetchReview,
  onRefetchTicket,
  onTicketUpdated,
}: HubspotTicketAutomatedReviewSectionProps) {
  const [isRerunning, setIsRerunning] = useState(false);
  const [overrideNotes, setOverrideNotes] = useState<Record<string, string>>(
    {},
  );
  const [overrideConclusions, setOverrideConclusions] = useState<
    Record<string, OverrideConclusion>
  >({});
  const [pendingOverrideCheckKey, setPendingOverrideCheckKey] = useState<
    string | null
  >(null);
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);
  // Keep the override implementation wired up, but hide the controls in the UI.
  const showOverrideControls = false;

  const refreshTicketShell = async () => {
    await onRefetchTicket();
    if (onTicketUpdated) {
      await onTicketUpdated();
    }
  };

  const handleRerun = async () => {
    if (!trpc?.client) {
      toast.error('TRPC context not initialized');
      return;
    }

    setIsRerunning(true);

    try {
      const result = await trpc.client.rerunHubspotTicketAutomatedReview.mutate(
        {
          ticketId,
          strategy: 'latest',
          sourceRunId: null,
        },
      );

      if (isRight(result)) {
        toast.success('Queued automated review check');
        await refreshTicketShell();
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to queue automated review');
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to queue automated review',
      );
    } finally {
      setIsRerunning(false);
    }
  };

  const handleOverride = async (
    check: HubspotTicketAutomatedReviewData['checks'][number],
    action: 'set' | 'revoke',
  ) => {
    if (!trpc?.client || !review) {
      toast.error('Automated review is not ready');
      return;
    }

    const note = (overrideNotes[check.checkKey] ?? '').trim();
    if (!note) {
      toast.error('Override note is required');
      return;
    }

    setPendingOverrideCheckKey(check.checkKey);

    try {
      const result =
        await trpc.client.setHubspotTicketAutomatedReviewOverride.mutate({
          runId: review.runId,
          checkKey: check.checkKey,
          action,
          effectiveConclusion:
            action === 'set'
              ? (overrideConclusions[check.checkKey] ??
                check.effectiveConclusion ??
                undefined)
              : undefined,
          note,
        });

      if (isRight(result)) {
        toast.success(action === 'set' ? 'Override saved' : 'Override revoked');
        await refreshTicketShell();
        await onRefetchReview();
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to update review override');
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update review override',
      );
    } finally {
      setPendingOverrideCheckKey(null);
    }
  };

  const collapsedSummary = summary ? (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${getAutomatedReviewBadgeClassName(summary.effectiveStatus)}`}
    >
      {getAutomatedReviewSummaryLabel(summary)}
    </span>
  ) : null;
  const logsLabel =
    review?.executor === 'github-actions' ? 'Workflow Logs' : 'Worker Logs';
  const completedCheckCount =
    review?.checks.filter((check) => check.executionStatus === 'Completed')
      .length ?? 0;
  const runningCheck =
    review?.checks.find((check) => check.executionStatus === 'Running') ?? null;
  const queueStateLabel =
    review?.status === 'Queued' ? 'Waiting for worker claim' : '—';

  const body = (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          {summary ? (
            <>
              <span
                className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${getAutomatedReviewBadgeClassName(summary.effectiveStatus)}`}
              >
                {getAutomatedReviewSummaryLabel(summary)}
              </span>
              {summary.hasOverrides && (
                <div className="text-xs text-amber-300">
                  Effective status includes active overrides.
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-neutral-400">
              No automated review run has been recorded yet.
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              void handleRerun();
            }}
            disabled={isRerunning}
            className="rounded-md border border-teal-500/30 bg-teal-500/10 px-3 py-2 text-sm font-medium text-teal-300 transition-colors hover:bg-teal-500/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRerunning ? 'Queueing...' : 'Run Check'}
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-2 md:grid-cols-2">
          <InfoRow
            label="Last Completed"
            value={
              summary.lastCompletedAt
                ? formatDateTimeWithTimeFirst(summary.lastCompletedAt)
                : '—'
            }
          />
          <InfoRow
            label="Progress"
            value={
              review
                ? `${completedCheckCount}/${review.checks.length} completed`
                : `${summary.effectiveFailedCount} failing / ${summary.effectiveTotalCount} completed`
            }
          />
          <InfoRow
            label="Current Check"
            value={runningCheck?.displayName ?? queueStateLabel}
          />
          <InfoRow
            label="Failing Checks"
            value={`${summary.effectiveFailedCount} / ${summary.effectiveTotalCount}`}
          />
          <InfoRow
            label="Policy"
            value={latestPolicyVersion ?? '—'}
            mono={true}
          />
          <InfoRow
            label="Input Hash"
            value={summary.latestReviewInputHash ?? '—'}
            mono={true}
          />
          <InfoRow
            label="Run ID"
            value={summary.latestRunId ?? '—'}
            mono={true}
          />
          <InfoRow
            label="Started"
            value={
              review?.startedAt
                ? formatDateTimeWithTimeFirst(review.startedAt)
                : '—'
            }
          />
          <InfoRow
            label="Completed"
            value={
              review?.completedAt
                ? formatDateTimeWithTimeFirst(review.completedAt)
                : '—'
            }
          />
          <InfoRow label="Executor" value={getReviewExecutorLabel(review)} />
        </div>
      )}

      {reviewError && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-sm text-red-300">
          {reviewError.message}
        </div>
      )}

      {review?.errorMessage && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-sm text-red-300">
          {review.errorMessage}
        </div>
      )}

      {review ? (
        <>
          <div className="overflow-hidden rounded-lg border border-neutral-700/50 bg-neutral-950/30">
            {review.checks.map((check, index) => {
              const selectedConclusion: OverrideConclusion =
                overrideConclusions[check.checkKey] ??
                check.effectiveConclusion ??
                OVERRIDE_CONCLUSIONS[0];
              const overrideNote =
                overrideNotes[check.checkKey] ?? check.activeOverrideNote ?? '';
              const details = stringifyReviewDetails(check.details);
              const {
                Icon,
                badgeStatus,
                iconClassName,
                label: statusLabel,
              } = getCheckStatusPresentation(check);
              const hasSupplementalContent = Boolean(
                check.activeOverrideNote || details || check.rawOutputExcerpt,
              );
              const hasAccordionContent =
                hasSupplementalContent || showOverrideControls;
              const isExpanded = expandedCheckId === check.id;
              const contentId = `automated-review-check-${check.id}`;

              return (
                <div
                  key={check.id}
                  className="border-b border-neutral-700/40 bg-neutral-900/20 p-3 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!hasAccordionContent) {
                        return;
                      }

                      setExpandedCheckId((current) =>
                        current === check.id ? null : check.id,
                      );
                    }}
                    disabled={!hasAccordionContent}
                    aria-expanded={hasAccordionContent ? isExpanded : undefined}
                    aria-controls={hasAccordionContent ? contentId : undefined}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${
                      check.displayName
                    }`}
                    className="w-full text-left disabled:cursor-default disabled:opacity-100"
                  >
                    <div className="flex gap-3">
                      <div className="pt-0.5">
                        <Icon
                          className={`h-5 w-5 shrink-0 fill-current ${iconClassName}`}
                        />
                      </div>
                      <div className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-sm border border-neutral-700 bg-neutral-900 px-1 text-[10px] font-semibold text-neutral-300">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-white">
                              {check.displayName}
                            </div>
                            <div className="text-xs text-neutral-400">
                              {check.summary}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex flex-wrap justify-end gap-2">
                              <span
                                className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${getAutomatedReviewBadgeClassName(
                                  badgeStatus,
                                )}`}
                              >
                                {statusLabel}
                              </span>
                              {check.executionStatus === 'Completed' &&
                                check.effectiveConclusion !==
                                  check.conclusion &&
                                check.conclusion && (
                                  <span className="inline-flex items-center rounded-md border border-amber-500/20 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-300">
                                    Raw: {check.conclusion}
                                  </span>
                                )}
                            </div>
                            <div className="flex size-5 shrink-0 items-center justify-center">
                              <ExpandMoreIcon
                                aria-hidden="true"
                                className={`h-5 w-5 fill-current text-neutral-500 transition-transform ${hasAccordionContent ? '' : 'invisible'} ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && hasAccordionContent && (
                    <div
                      id={contentId}
                      className="space-y-2 border-t border-neutral-700/40 pt-3 pl-8"
                    >
                      {check.activeOverrideNote && (
                        <div className="rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
                          Override note: {check.activeOverrideNote}
                        </div>
                      )}

                      {details && (
                        <pre className="overflow-x-auto rounded-md border border-neutral-700/40 bg-neutral-950 p-3 text-xs text-neutral-300">
                          {details}
                        </pre>
                      )}

                      {check.rawOutputExcerpt && (
                        <pre className="overflow-x-auto rounded-md border border-neutral-700/40 bg-neutral-950 p-3 text-xs text-neutral-300">
                          {check.rawOutputExcerpt}
                        </pre>
                      )}

                      {(check.startedAt || check.completedAt) && (
                        <div className="grid gap-2 text-xs text-neutral-400 md:grid-cols-2">
                          <div>
                            Started:{' '}
                            {check.startedAt
                              ? formatDateTimeWithTimeFirst(check.startedAt)
                              : '—'}
                          </div>
                          <div>
                            Completed:{' '}
                            {check.completedAt
                              ? formatDateTimeWithTimeFirst(check.completedAt)
                              : '—'}
                          </div>
                        </div>
                      )}

                      {showOverrideControls && (
                        <div className="space-y-2 border-t border-neutral-700/40 pt-3">
                          <div className="grid gap-2 md:grid-cols-[180px_minmax(0,1fr)]">
                            <select
                              value={selectedConclusion}
                              onChange={(event) =>
                                setOverrideConclusions((prev) => ({
                                  ...prev,
                                  [check.checkKey]: event.target
                                    .value as OverrideConclusion,
                                }))
                              }
                              className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-white focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                              style={{ colorScheme: 'dark' }}
                            >
                              {OVERRIDE_CONCLUSIONS.map((conclusion) => (
                                <option key={conclusion} value={conclusion}>
                                  {conclusion}
                                </option>
                              ))}
                            </select>
                            <textarea
                              aria-label={`Override note for ${check.displayName}`}
                              value={overrideNote}
                              onChange={(event) =>
                                setOverrideNotes((prev) => ({
                                  ...prev,
                                  [check.checkKey]: event.target.value,
                                }))
                              }
                              rows={2}
                              placeholder="Explain why this check should be overridden"
                              className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                void handleOverride(check, 'set');
                              }}
                              disabled={
                                pendingOverrideCheckKey === check.checkKey
                              }
                              className="rounded-md border border-teal-500/30 bg-teal-500/10 px-3 py-2 text-sm font-medium text-teal-300 transition-colors hover:bg-teal-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {pendingOverrideCheckKey === check.checkKey
                                ? 'Saving...'
                                : 'Set Override'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                void handleOverride(check, 'revoke');
                              }}
                              disabled={
                                pendingOverrideCheckKey === check.checkKey ||
                                !check.activeOverrideId
                              }
                              className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm font-medium text-neutral-200 transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Revoke Override
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {(review.runHtmlUrl ||
            review.logsUrl ||
            review.artifacts.length > 0) && (
            <div className="space-y-2 border-t border-neutral-700/40 pt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                Artifacts
              </div>
              <div className="flex flex-wrap gap-2">
                {review.runHtmlUrl && (
                  <a
                    href={review.runHtmlUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-sky-300 transition-colors hover:bg-neutral-800"
                  >
                    Open GitHub Run
                  </a>
                )}
                {review.logsUrl && (
                  <a
                    href={review.logsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-teal-300 transition-colors hover:bg-neutral-800"
                  >
                    {logsLabel}
                  </a>
                )}
                {review.artifacts.map((artifact) =>
                  artifact.url ? (
                    <a
                      key={artifact.id}
                      href={artifact.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 transition-colors hover:bg-neutral-800"
                    >
                      {artifact.fileName}
                    </a>
                  ) : (
                    <span
                      key={artifact.id}
                      className="rounded-md border border-neutral-700/60 bg-neutral-900/60 px-3 py-2 text-sm text-neutral-500"
                    >
                      {artifact.fileName}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-neutral-400">
          Detailed automated review results will appear here once a run is
          available.
        </div>
      )}
    </div>
  );

  return (
    <LoadingSection isLoading={isReviewLoading}>
      {alwaysExpanded ? (
        <PanelSection title="Automated review" trailing={collapsedSummary}>
          {body}
        </PanelSection>
      ) : (
        <CollapsibleSection
          title="Automated Review"
          isExpanded={isExpanded}
          onToggle={onToggle}
          collapsedSummary={collapsedSummary}
        >
          {body}
        </CollapsibleSection>
      )}
    </LoadingSection>
  );
}
